/**
 * Enveloppe des cinq écrans de la campagne (CAM-01 à CAM-06).
 *
 * Il résout une bonne fois la saison de travail — URL, puis mémoire du poste,
 * puis la saison la plus probable — affiche l’en-tête permanent (saison, phase,
 * les deux dates d’ouverture) et le fil des étapes, puis laisse la place à
 * l’écran courant.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router'

import {
  LIBELLES_PHASE_SAISON,
  type PhaseSaison,
  type Saison,
} from '@tb/shared'

import { estApiError } from '../../lib/api'
import { formatDateSeule } from '../../lib/format'
import { cn } from '../../lib/cn'
import { Badge, Card, ChargementPage, Select, TON_PHASE_SAISON } from '../../ui'
import { ContexteCampagne, useCampagne, type ValeurCampagne } from './contexte'
import { useSaisons, useSuiviCampagne } from './hooks'
import { ETAPES_CAMPAGNE, urlEtape } from './libelles'
import {
  lireDerniereSaison,
  lireSaisonN1,
  memoriserSaison,
  memoriserSaisonN1,
} from './stockage'

const PARAM_SAISON = 'saison'
const PARAM_SAISON_N1 = 'saisonN1'

/**
 * Ordre de travail des saisons : la plus récente d’abord. Les libellés sont du
 * type « 2027-2028 », un tri texte descendant suffit et reste lisible.
 */
function trierSaisons(saisons: readonly Saison[]): Saison[] {
  return [...saisons].sort((a, b) => b.libelle.localeCompare(a.libelle, 'fr', { numeric: true }))
}

/** La saison sur laquelle on travaille par défaut : celle qui n’est pas finie. */
function saisonProbable(saisons: readonly Saison[]): Saison | null {
  const triees = trierSaisons(saisons)
  const priorites: readonly PhaseSaison[] = ['interne', 'preparation', 'externe']
  for (const phase of priorites) {
    const trouvee = triees.find((saison) => saison.phase === phase)
    if (trouvee !== undefined) return trouvee
  }
  return triees[0] ?? null
}

export function LayoutCampagne() {
  const [parametres, definirParametres] = useSearchParams()
  const requete = useSaisons()

  const saisons = useMemo(() => trierSaisons(requete.data ?? []), [requete.data])
  const saisonsLisibles = !(estApiError(requete.error) && requete.error.estInterdit)

  const saisonUrl = parametres.get(PARAM_SAISON)
  const saisonN1Url = parametres.get(PARAM_SAISON_N1)

  /* --------------------------------------------------------------------- */
  /* Résolution de la saison de travail                                     */
  /* --------------------------------------------------------------------- */

  const definirSaison = useCallback(
    (saisonId: string) => {
      memoriserSaison(saisonId)
      definirParametres(
        (precedents) => {
          const suivants = new URLSearchParams(precedents)
          suivants.set(PARAM_SAISON, saisonId)
          // La saison N-1 dépend de la saison N : on ne la traîne pas.
          suivants.delete(PARAM_SAISON_N1)
          return suivants
        },
        { replace: true },
      )
    },
    [definirParametres],
  )

  const definirSaisonN1 = useCallback(
    (saisonN1Id: string) => {
      if (saisonUrl !== null) memoriserSaisonN1(saisonUrl, saisonN1Id)
      definirParametres(
        (precedents) => {
          const suivants = new URLSearchParams(precedents)
          suivants.set(PARAM_SAISON_N1, saisonN1Id)
          return suivants
        },
        { replace: true },
      )
    },
    [definirParametres, saisonUrl],
  )

  // Aucune saison dans l’URL : on reprend celle du poste, sinon la plus probable.
  useEffect(() => {
    if (saisonUrl !== null) return
    const memorisee = lireDerniereSaison()
    const candidate =
      (memorisee !== null && saisons.some((saison) => saison.id === memorisee)
        ? memorisee
        : null) ?? saisonProbable(saisons)?.id ?? memorisee
    if (candidate !== null && candidate !== undefined) definirSaison(candidate)
  }, [saisonUrl, saisons, definirSaison])

  // Saison N-1 : mémoire du poste, sinon la saison qui précède immédiatement.
  useEffect(() => {
    if (saisonUrl === null || saisonN1Url !== null) return
    const memorisee = lireSaisonN1(saisonUrl)
    if (memorisee !== null && saisons.some((saison) => saison.id === memorisee)) {
      definirSaisonN1(memorisee)
      return
    }
    const index = saisons.findIndex((saison) => saison.id === saisonUrl)
    const precedente = index >= 0 ? saisons[index + 1] : undefined
    if (precedente !== undefined) definirSaisonN1(precedente.id)
  }, [saisonUrl, saisonN1Url, saisons, definirSaisonN1])

  const valeur: ValeurCampagne = useMemo(
    () => ({
      saisonId: saisonUrl,
      saison: saisons.find((saison) => saison.id === saisonUrl) ?? null,
      saisonN1Id: saisonN1Url,
      saisonN1: saisons.find((saison) => saison.id === saisonN1Url) ?? null,
      saisons,
      saisonsLisibles,
      chargementSaisons: requete.isPending,
      definirSaison,
      definirSaisonN1,
    }),
    [
      saisonUrl,
      saisonN1Url,
      saisons,
      saisonsLisibles,
      requete.isPending,
      definirSaison,
      definirSaisonN1,
    ],
  )

  if (requete.isPending && saisonUrl === null) return <ChargementPage libelle="Chargement des saisons…" />

  return (
    <ContexteCampagne value={valeur}>
      <div className="flex flex-col gap-6">
        <EnteteCampagne />
        <FilEtapes saisonId={saisonUrl} />
        <Outlet />
      </div>
    </ContexteCampagne>
  )
}

/* -------------------------------------------------------------------------- */
/* En-tête permanent                                                          */
/* -------------------------------------------------------------------------- */

function EnteteCampagne() {
  const { saisonId, saison, saisons, saisonsLisibles, definirSaison } = useCampagne()
  const suivi = useSuiviCampagne(saisonId)

  // La phase vient du suivi (accessible au guichet) ; le catalogue, réservé à
  // l’administration, ne complète que les dates.
  const phase: PhaseSaison | null = suivi.data?.phase ?? saison?.phase ?? null
  const libelle = suivi.data?.saisonLibelle ?? saison?.libelle ?? null

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Campagne de réabonnement</h1>
            <p className="mt-1 text-sm text-slate-600">
              {libelle === null
                ? 'Choisissez la saison que vous ouvrez à l’abonnement.'
                : libelle}
              {phase !== null && (
                <>
                  {' · '}
                  <Badge ton={TON_PHASE_SAISON[phase]} point>
                    {LIBELLES_PHASE_SAISON[phase]}
                  </Badge>
                </>
              )}
            </p>
          </div>

          <div className="min-w-64">
            {saisonsLisibles ? (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700">Saison en campagne</span>
                <Select
                  value={saisonId ?? ''}
                  placeholder="Choisir une saison…"
                  options={saisons.map((element) => ({
                    valeur: element.id,
                    libelle: `${element.libelle} — ${LIBELLES_PHASE_SAISON[element.phase]}`,
                  }))}
                  onChange={(evenement) => { definirSaison(evenement.target.value) }}
                />
              </label>
            ) : (
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                La liste des saisons est réservée à l’administration. Vous travaillez sur la
                saison qui vous a été transmise ; demandez le lien à jour si besoin.
              </p>
            )}
          </div>
        </div>

        {saison !== null && (
          <dl className="flex flex-wrap gap-x-8 gap-y-2 border-t border-slate-100 pt-3 text-sm">
            <div className="flex gap-2">
              <dt className="text-slate-500">Ouverture interne</dt>
              <dd className="font-medium text-slate-900">
                {formatDateSeule(saison.dateOuvertureInterne)}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">Ouverture externe</dt>
              <dd className="font-medium text-slate-900">
                {formatDateSeule(saison.dateOuvertureExterne)}
              </dd>
            </div>
            {phase !== null && (
              <div className="flex gap-2">
                <dt className="text-slate-500">Sièges gelés</dt>
                <dd className="font-medium text-slate-900">{suivi.data?.siegesGeles ?? '…'}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Fil des étapes                                                             */
/* -------------------------------------------------------------------------- */

function FilEtapes({ saisonId }: { readonly saisonId: string | null }) {
  const { pathname } = useLocation()

  return (
    <nav aria-label="Étapes de la campagne">
      <ol className="flex flex-wrap gap-2">
        {ETAPES_CAMPAGNE.map((etape) => {
          const cible = urlEtape(etape.segment, saisonId)
          const chemin = cible.split('?')[0] ?? ''
          const actif = pathname === chemin || (etape.segment === '' && pathname === `${chemin}/`)
          return (
            <li key={etape.id}>
              <NavLink
                to={cible}
                end={etape.segment === ''}
                title={etape.description}
                className={cn(
                  // `min-h-11` : les quatre étapes de la campagne se cliquent
                  // aussi au doigt, depuis un téléphone.
                  'flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors sm:min-h-0',
                  actif
                    ? 'border-rideau-600 bg-rideau-50 font-semibold text-rideau-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                )}
              >
                {etape.numero !== null && (
                  <span
                    className={cn(
                      'inline-flex size-5 items-center justify-center rounded-full text-xs font-semibold',
                      actif ? 'bg-rideau-600 text-white' : 'bg-slate-100 text-slate-600',
                    )}
                    aria-hidden="true"
                  >
                    {etape.numero}
                  </span>
                )}
                {etape.libelle}
              </NavLink>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

